<template>
    <div>
        <div class="flex justify-between">
            <div v-if="showHeader" class="flex flex-1" :class="[smallHeader ? 'my-3' : 'mb-5']">
                <h1 class="page-header" :class="{ 'text-2xl': smallHeader }">Subsidiaries</h1>
                <div class="ml-3 flex items-center text-gray-600">
                    <div :class="[smallHeader ? 'text-lg' : 'text-2xl']">/</div>
                    <div class="ml-2 text-xl" :class="[smallHeader ? 'pt-2px' : 'pt-1']">{{ subsidiaries.filtered.length }} of {{ subsidiaries.all.length }}</div>
                </div>
            </div>
            <div class="flex flex-1 items-center justify-end">
                <div v-if="showFilters" class="flex items-center">
                    <filters
                        data-type="subsidiaries"
                        sort-by="name"
                        portal="no-subsidiaries-found"
                        :show-client-filter="false"
                        :selected-view-option="layout"
                        @view-change="layout = $event">
                    </filters>
                    <slot name="action-buttons"></slot>
                </div>
                <button
                    @click="$modal.show('manage-subsidiaries')"
                    type="button"
                    class="ml-4 flex items-center btn btn-small btn-blue focus:outline-none">
                        <inline-svg name="edit-white"></inline-svg>
                        Manage Subsidiaries
                </button>
            </div>
        </div>

        <div v-if="layout == 'grid'" class="flex flex-wrap gap-x-5 gap-y-3">
            <div class="card no-scale border rounded-md" v-for='(subsidiary, subsidiaryIndex) in subsidiaries.filtered' :key="subsidiaryIndex">
                <div class="w-72 h-full box box-green hover:bg-gray-100">
                    <div class="relative px-5 py-6 flex flex-col">
                        <div class="absolute top-3 right-2 flex items-center justify-between space-x-3 z-50">
                            <button
                                type="button"
                                @click.prevent="showEditSubsidiary(row.id)"
                                class="cursor-pointer"
                                title="Edit Subsidiary">
                                    <inline-svg name="edit"></inline-svg>
                            </button>
                            <button
                                type="button"
                                @click.prevent="confirmDeleteSubsidiary(row.id)"
                                class="cursor-pointer"
                                title="Delete Subsidiary">
                                    <inline-svg name="trash"></inline-svg>
                            </button>
                        </div>
                        <div class="text-xl">{{ subsidiary.name }}</div>
                        <div class="mt-2 flex flex-1 flex-col">
                            <div class="mt-2 text-gray-700">
                                <p class="text-lg font-semibold">{{ subsidiary.address | truncate(20) }}</p>
                                <p>
                                    {{ subsidiary.city }}, {{ subsidiary.state }} {{ subsidiary.postal_code }}
                                </p>
                                <p class="mt-1">{{ subsidiary.country.name }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="layout == 'table' && subsidiaries.filtered.length">
            <table class="mt-2">
                <thead class="cursor-pointer">
                    <th class="pl-4">Name</th>
                    <th class="px-6">Address</th>
                    <th class="px-6">City</th>
                    <th class="px-6">State</th>
                    <th class="px-6">Zip</th>
                    <th class="px-6"></th>
                </thead>
                <tbody>
                    <tr v-for="row in orderBy(subsidiaries.filtered, 'name')" :key="row.id">
                        <td class="pl-4">{{ row.name }}</td>
                        <td class="px-6">{{ row.address }}</td>
                        <td class="px-6">{{ row.city }}</td>
                        <td class="px-6">{{ row.state }}</td>
                        <td class="px-6">{{ row.postal_code }}</td>           
                        <td class="text-center px-6">
                            <div class="flex justify-end space-x-3">
                                <button
                                    @click="showEditSubsidiary(row.id)"
                                    title="Edit Subsidiary"
                                    class="btn btn-action">
                                        Edit
                                </button>
                                <button
                                    @click="confirmDeleteSubsidiary(row.id)"
                                    class="btn btn-action"
                                    title="Delete Subsidiary">
                                        Delete
                                </button>
                            </div>
                        </td>                        
                    </tr>
                </tbody>
            </table>
        </div>

        <portal-target name="no-subsidiaries-found"></portal-target>

        <notifications classes="notification" position="bottom right"></notifications>

        <modal name="manage-subsidiaries" width="60%" height="auto" click-to-close :scrollable="true" draggable=".modal-header">
            <manage-subsidiaries :for-client-id="clientId" :subsidiary-id="selectedSubsidiaryId"></manage-subsidiaries>
        </modal>
    </div>
</template>

<script>   
    import { mapState } from 'vuex';

    export default {
        props: {
            subsidiaryData: {
              type: Array,
              required: true
            },

            forClientId: {
                type: Number,
                required: true
            },

            showHeader: {
              type: Boolean,
              default: true
            },

            smallHeader: {
              type: Boolean,
              default: false
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            view: {
                type: String,
                default: 'grid'
            },

        },

        data() {
            return {
                selectedSubsidiaryId: null,
                clientId: this.forClientId,
                layout: this.view
            }
        },

        mounted() {
            if (localStorage.getItem('reopt_subsidiaries_layout')) {
                this.layout = localStorage.getItem('reopt_subsidiaries_layout');
            }

            this.subsidiaries.all = this.subsidiaryData;
            this.subsidiaries.filtered = this.subsidiaryData;

            this.$emit('filterRequest');

            this.$eventHub.$on('updateSubsidiaries', () => {
                this.$store.dispatch('loadSubsidiaries', this.forClientId);
            });
        },

        computed: {
            ...mapState(['subsidiaries']),
        },

        methods: {
            showEditSubsidiary(subsidiaryId) {
                this.selectedSubsidiaryId = subsidiaryId;

                this.$modal.show('manage-subsidiaries');
            },

            confirmDeleteSubsidiary(subsidiaryId) {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this subsidiary?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteSubsidiary(subsidiaryId);
                    }
                });
            },

            deleteSubsidiary(subsidiaryId) {
                if (subsidiaryId == 'new') {
                    _.remove(this.subsidiaries.all, function(row) {
                      return row.id == 'new';
                    });

                    return;
                }

                axios.delete('/subsidiaries/' + subsidiaryId)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });

                        this.$eventHub.$emit('reloadClient');
                    })
                    .catch(error => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            },
        }
    }
</script>